import React from 'react';

import {
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import PositionPill from './PositionPill';
import { UI, dateShort, stageDefinitions, tableSx } from './operationalUi';
import type { FlashFilters, FlashSnapshot, OperationalResult } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  data?: FlashSnapshot;
  filters: FlashFilters;
  result?: OperationalResult | null;
  onExport: () => void;
}
export default function MethodDrawer({ open, onClose, data, filters, result, onExport }: Props) {
  const definitions = [
    ['Impressions per day', 'Search impressions ÷ observed campaign-days.'],
    ['Offer views per day', 'Offer-page views ÷ observed campaign-days.'],
    ['Impressions to offer views', 'Offer views ÷ search impressions. A directional ratio.'],
    ['Packages per day', 'Packages purchased ÷ observed campaign-days.'],
    ['Offer views to packages', 'Packages purchased ÷ offer views.'],
    ['Margin per day', 'Adjusted margin before refunds ÷ observed campaign-days.'],
    ['Margin per offer view', 'Adjusted margin before refunds ÷ offer views.'],
  ];
  const sources = [
    ['reporting.sales', 'Packages and adjusted margin before refunds'],
    ['analytics.events_daily_by_session_offer', 'Offer-page views'],
    ['analytics.search_impressions_detailed', 'Search impressions'],
    ['stage.stage_campaign_schedule', 'Campaign dates and status'],
    ['datawarehouse.dim_offer', 'Country, destination, type and commercial territory'],
    ['analytics.pcm_tracker_offers', 'Quarter sales attribution (PCM)'],
  ];
  const heading = (text: string) => (
    <Typography variant="subtitle2" sx={{ fontSize: 15, fontWeight: 600, color: UI.teal, mb: 1 }}>
      {text}
    </Typography>
  );
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 510, maxWidth: '92vw', bgcolor: UI.sand } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2.5, bgcolor: UI.header, color: UI.white }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>Method and sources</Typography>
        <Button sx={{ color: UI.white }} aria-label="Close method and sources" onClick={onClose}>
          Close
        </Button>
      </Stack>
      <Stack spacing={2.5} divider={<Divider />} sx={{ p: 2.5, overflowY: 'auto' }}>
        {result && filters.view !== 'campaign' && (
          <Box>
            {heading('Coverage in this view')}
            <Typography sx={{ fontSize: 14 }}>
              {result.coverage.compared}/{result.coverage.total} campaigns have peers; {result.counts.tooEarly} are too
              early to assess.
            </Typography>
            <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
              Compared campaigns cover {Math.round((result.coverage.marginShare || 0) * 100)}% of recorded margin.{' '}
              {result.coverage.onCountryPeers} use country peers.
            </Typography>
            <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
              Without peers: {result.coverage.uncomparedReasons.unknown_destination || 0} unknown destination;{' '}
              {result.coverage.uncomparedReasons.too_few_peers || 0} too few peers;{' '}
              {result.coverage.uncomparedReasons.no_sales_rows || 0} no sales records.
            </Typography>
            <Table size="small" sx={{ ...tableSx, mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Stage</TableCell>
                  <TableCell>Compared</TableCell>
                  <TableCell>Too early</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(result.stageCounts)
                  .filter(([id]) => stageDefinitions.some((stage) => stage.id === id))
                  .map(([id, stage]) => (
                    <TableRow key={id}>
                      <TableCell>{stageDefinitions.find((stage) => stage.id === id)?.label || id}</TableCell>
                      <TableCell>{stage.compared}</TableCell>
                      <TableCell>{stage.tooEarly || 0}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <Box>
          {heading('Flash scope')}
          <Typography sx={{ fontSize: 14 }}>
            Hotel products only: product_bk = hotel on the bookable offer, with an eligible Flash campaign schedule.
            Linked service offers take precedence over generic Salesforce offer records.
          </Typography>
        </Box>
        <Box>
          {heading('Seven measures')}
          <Stack spacing={1}>
            {definitions.map(([name, definition], i) => (
              <Box key={name}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {i + 1}. {name}
                </Typography>
                <Typography sx={{ fontSize: 13, color: UI.muted }}>{definition}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
        <Box>
          {heading('Previous Flash run')}
          <Typography sx={{ fontSize: 14 }}>
            The immediately earlier run for the same verified property and campaign type. Compare the first N launch
            days in both, capped by the shorter run. An exact same-offer match is separately labelled when property
            identity is missing. Ambiguous or overlapping runs are unavailable.
          </Typography>
          <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
            Current filters select campaigns to assess; prior history is not removed by current launch dates or PCM. The
            same website market applies to both. Absolute observation dates withhold this view. History starts 1 January
            2026; no match does not mean first-ever run. Portfolio results use source-valid pairs with 7+ aligned days,
            with a separate coverage count for each measure.
          </Typography>
        </Box>
        <Box>
          {heading('Peer rule')}
          <Typography sx={{ fontSize: 14 }}>
            Completed, mature {filters.cohort === 'primary' ? '28–42 day' : 'all-duration'} campaigns of the same
            destination and campaign type, excluding the selected campaign. When there are fewer than {filters.minN}{' '}
            peers with sales rows, a same-country/type group can be used and is visibly labelled.
          </Typography>
          <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
            Active filters also constrain peers. Each measure needs its own observed source data and minimum valid
            sample. Product eligibility is hotel-only; campaign-type groups remain separate. Portfolio bars count
            campaigns at each stage, with each campaign assessed against its own peers; early campaigns stay outside the
            position counts.
          </Typography>
        </Box>
        <Box>
          {heading('Position bands')}
          <Stack spacing={1}>
            {[
              ['below', 'Below the first quartile of its peers.'],
              ['in_line', 'Between the first and third quartiles.'],
              ['above', 'Above the third quartile.'],
              ['unscored', 'Fewer than the required valid peers or missing source coverage.'],
            ].map(([position, definition]) => (
              <Box key={position}>
                <PositionPill position={position} />
                <Typography sx={{ fontSize: 13, mt: 0.5 }}>{definition}</Typography>
              </Box>
            ))}
            <Typography sx={{ fontSize: 13 }}>
              Too early: fewer than 7 observed days; excluded from the headline. No sales: at least 7 view-observed days
              and 500 views without sales rows flags a campaign for investigation. Packages and margin stay unknown; the
              campaign is excluded from the margin ranking and rate-gap total. Check sales ingestion and offer mapping.
            </Typography>
            <Typography sx={{ fontSize: 13, color: UI.muted }}>
              Quartile bands displayed from 17 October 2026. The export contains the additional statistical fields for
              each measure.
            </Typography>
          </Stack>
        </Box>
        <Box>
          {heading('Behind peer median pace')}
          <Typography sx={{ fontSize: 14 }}>
            For well-below campaigns with 7+ observed days, add the peer median margin/day less actual margin/day,
            multiplied by the smaller of days left and 28. This is a rate gap before refunds at today’s rates, not a
            forecast or a target.
          </Typography>
        </Box>
        <Box>
          {heading('Weekly trend')}
          <Typography sx={{ fontSize: 14 }}>
            Live and completed campaigns, grouped Monday–Sunday. All Flash is the default. More filters offers
            searchable campaign exclusions for the weekly headline, charts and weekly CSV only. Campaign lists, peer
            scores and Previous Flash comparisons keep their original scope. Hollow points mark partial weeks.
          </Typography>
          <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
            Campaign-days count recorded activity days. Margin is before refunds. Adjacent full-week periods form the
            weekly headline; recorded volume and margin per day can move in different directions.
          </Typography>
        </Box>
        <Box>
          {heading('Filters and ownership')}
          <Typography sx={{ fontSize: 14 }}>
            {data?.sourceCapabilities?.pcm?.definition || 'PCM is quarter sales attribution, not an owner roster.'}{' '}
            Owner (Salesforce) is shown separately when the quarter tracker has no record; it never changes the PCM
            filter or roll-up.
          </Typography>
          <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
            Territory is current Salesforce commercial mapping. Metadata describes the current snapshot, not the
            campaign’s launch-time owner. Website-market selection withholds both impression measures; launch-age
            trajectory uses the selected website market’s recorded packages and margin.
          </Typography>
        </Box>
        <Box>
          {heading('Sources')}
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Table</TableCell>
                <TableCell>Role</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sources.map(([name, role]) => (
                <TableRow key={name}>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{name}</TableCell>
                  <TableCell>{role}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
            Coverage is shown on the page. Missing source records remain unknown and are not assumed zero.
          </Typography>
        </Box>
        <Box>
          {heading('Limits and refresh')}
          <Typography sx={{ fontSize: 14 }}>
            Descriptive comparisons, not causal findings. Price, availability, discount, refunds and channel mix are
            outside this snapshot.
          </Typography>
          <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
            Facts through {dateShort(data?.meta.observationCutoff)}. Snapshot generated{' '}
            {data?.meta.generatedAt || 'unavailable'}. Weekly charts use recorded campaign-days, not contracted deals,
            and adjacent full-week windows.
          </Typography>
        </Box>
        <Box>
          {heading('Figures to verify')}
          <Typography sx={{ fontSize: 13 }}>
            Christie’s original workbook v the reconstructed September source records:
          </Typography>
          <Typography sx={{ fontSize: 13, mt: 1 }}>
            Kempinski: A$222.89 margin difference and 9 offer views. Across the workbook: 4,349 offer views.
          </Typography>
          <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1 }}>
            These historical differences remain unresolved. Owner: Ming. No resolution date is agreed. Current campaign
            figures use the snapshot sources above; they are not replacements for the original workbook records.
          </Typography>
        </Box>
        <Button variant="contained" onClick={onExport}>
          Export active scope CSV
        </Button>
      </Stack>
    </Drawer>
  );
}
